import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Alert,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const Add = ({ onRecordAdded }) => {
  const { addData: addFirestoreData, data: existingDivisions } =
    useFirestore("Divisions");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [nameError, setNameError] = useState("");
  const initialFormData = {
    DivisionID: "",
    DivisionName: "",
    Active: true, // Default to Active
  };
  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear duplicate error as user types
    if (name === "DivisionName") {
      setNameError("");
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  const handleSubmit = async () => {
    // Duplicate name check (case-insensitive)
    const trimmedName = (formData.DivisionName || "").trim();
    if (!trimmedName) {
      setNameError("Division Name is required.");
      return;
    }
    const duplicate = (existingDivisions || []).some(
      (d) =>
        (d.DivisionName || "").trim().toLowerCase() ===
        trimmedName.toLowerCase(),
    );
    if (duplicate) {
      setNameError(`A division named "${trimmedName}" already exists.`);
      return;
    }

    setLoading(true);
    setSnackbarMessage("");
    try {
      await addFirestoreData({ ...formData, DivisionName: trimmedName });
      setSnackbarMessage("Division added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setFormData(initialFormData);
      setNameError("");
      setOpen(false);
      onRecordAdded();
    } catch (error) {
      console.error("Error adding record:", error);
      setSnackbarMessage(`Error adding division: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setNameError("");
  };

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ marginBottom: "10px", color: "white" }}
      >
        Add Division
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Add Division
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Division ID"
                name="DivisionID"
                value={formData.DivisionID}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Division Name"
                name="DivisionName"
                value={formData.DivisionName}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!nameError}
                helperText={nameError}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active === true}
                    onChange={handleCheckboxChange}
                    name="Active"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Add;
